import React, { useCallback, useMemo, useState } from "react";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Avatar, InputText, Text } from "@performyard/py-components";

import type { OrgEmployee } from "../types";

import { getDisplayFields } from "../data";

export function SearchPanel({
	allEmployees,
	onSelect,
}: {
	allEmployees: OrgEmployee[];
	onSelect: (employeeId: string) => void;
}) {
	const [query, setQuery] = useState("");

	const filteredEmployees = useMemo(() => {
		const trimmedQuery = query.trim().toLowerCase();
		if (!trimmedQuery) return [];
		const matches: OrgEmployee[] = [];

		for (const employee of allEmployees) {
			if (matches.length >= 10) break;
			const fieldValues = employee.fields.map((field) => field.value).join(" ");
			const haystack = [employee.id, employee.name, fieldValues].join(" ").toLowerCase();

			if (haystack.includes(trimmedQuery)) {
				matches.push(employee);
			}
		}

		return matches;
	}, [allEmployees, query]);

	const handleSelect = useCallback(
		(employeeId: string) => {
			onSelect(employeeId);
			setQuery("");
		},
		[onSelect]
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Escape") {
				setQuery("");
			}
			if (event.key === "Enter" && filteredEmployees[0]) {
				handleSelect(filteredEmployees[0].id);
			}
		},
		[filteredEmployees, handleSelect]
	);

	return (
		<div className="relative z-10 w-[min(360px,100%)]">
			<InputText
				leftIcon={faMagnifyingGlass}
				value={query}
				onChange={setQuery}
				onKeyDown={handleKeyDown}
				placeholder="Search Employees"
				aria-label="Search Employees"
				clearable
				dangerouslySetClassName="min-h-10 bg-white shadow-[0_8px_22px_rgba(20,32,44,0.12)]"
			/>
			{query.trim() && (
				<div className="absolute top-[calc(100%+6px)] left-0 z-[60] w-full overflow-hidden rounded-md border border-neutral-3 bg-white shadow-[0_14px_32px_rgba(20,32,44,0.18)]">
					{filteredEmployees.length === 0 ? (
						<div className="px-3 py-4 text-center">
							<Text size="caption" color="neutral-6">
								No employees found
							</Text>
						</div>
					) : (
						filteredEmployees.map((employee) => {
							const displayFields = getDisplayFields(employee);
							const fieldSummary = displayFields
								.flatMap((field) => (field.value ? [field.value] : []))
								.join(" - ");
							const placeholder = displayFields[0]
								? `No ${displayFields[0].name.toLowerCase()}`
								: "No title";

							return (
								<button
									key={employee.id}
									type="button"
									className="flex w-full cursor-pointer items-center gap-2.5 border-0 bg-white px-3 py-2.5 text-left hover:bg-primary-1"
									onClick={() => handleSelect(employee.id)}
								>
									<Avatar id={employee.id} name={employee.name} size={32} />
									<span className="flex min-w-0 flex-col gap-0.5">
										<Text
											size="paragraph-3"
											weight="bold"
											color="neutral-10"
											wrap={false}
											dangerouslySetClassName="py-text-truncate"
										>
											{employee.name}
										</Text>
										<Text
											size="caption"
											color="neutral-6"
											wrap={false}
											dangerouslySetClassName="py-text-truncate"
										>
											{fieldSummary || placeholder}
										</Text>
									</span>
								</button>
							);
						})
					)}
				</div>
			)}
		</div>
	);
}
